import React, { useState } from 'react';
import { signIn } from 'aws-amplify/auth';
import {
  Container,
  Header,
  Form,
  FormField,
  Input,
  Button,
  Alert,
  SpaceBetween,
  Link
} from '@cloudscape-design/components';

export const LoginForm = ({ onSuccess, onSwitchToSignUp, onSwitchToFederated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signIn({ 
        username: email, 
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' }
      });

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const { confirmSignIn } = await import('aws-amplify/auth');
      await confirmSignIn({ challengeResponse: newPassword });
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  if (needsNewPassword) {
    return (
      <Container
        header={<Header variant="h1">Set New Password</Header>}
      >
        <form onSubmit={handleNewPassword}>
          <SpaceBetween size="l">
            {error && (
              <Alert type="error" dismissible onDismiss={() => setError('')}>
                {error}
              </Alert>
            )}
            <Alert type="info">
              Your account requires a new password. Please set one below.
            </Alert>
            <FormField label="New Password">
              <Input
                value={newPassword}
                onChange={({ detail }) => setNewPassword(detail.value)}
                type="password"
                required
              />
            </FormField>
            <FormField label="Confirm New Password">
              <Input
                value={confirmNewPassword}
                onChange={({ detail }) => setConfirmNewPassword(detail.value)}
                type="password"
                required
              />
            </FormField>
            <Button variant="primary" loading={loading} formAction="submit">
              Set Password & Sign In
            </Button>
          </SpaceBetween>
        </form>
      </Container>
    );
  }

  return (
    <Container
      header={<Header variant="h1" style={{ textAlign: 'center' }}>Sign in to ResearchIQ</Header>}
    >
      <form onSubmit={handleSubmit}>
        <SpaceBetween size="l">
          {error && (
            <Alert type="error" dismissible onDismiss={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <FormField label="Email">
            <Input
              value={email}
              onChange={({ detail }) => setEmail(detail.value)}
              type="email"
              required
            />
          </FormField>

          <FormField label="Password">
            <Input
              value={password}
              onChange={({ detail }) => setPassword(detail.value)}
              type="password"
              required
            />
          </FormField>

          <Button
            variant="primary"
            loading={loading}
            formAction="submit"
          >
            Sign in
          </Button>

          {onSwitchToFederated && (
            <Link onFollow={onSwitchToFederated}>
              ← Back to SSO login
            </Link>
          )}
        </SpaceBetween>
      </form>
    </Container>
  );
};
